import type { AllChainKey } from './allChains';
import { allChains } from './allChains';
import type {
  ChainKey,
  Chains,
  EvmChainKey,
  EvmMainnetChainKey,
  EvmMainnetWalletChainKey,
  EvmVaultChainKey,
  MainnetChainKey,
  NativeChainKey,
  SvmChainKey,
  SvmMainnetWalletChainKey,
  UnsupportedChainKey,
  UnsupportedChains,
  VaultChainKey,
  WalletChainKey,
} from './derivedTypes';

// Config objects ----------------------------

// The primary chains object (actually supportedChains)
export const chains = Object.fromEntries(
  Object.entries(allChains).filter(([_, c]) => c.isSupported)
) as Chains;

// Everything else
export const unsupportedChains = Object.fromEntries(
  Object.entries(allChains).filter(([_, c]) => !c.isSupported)
) as UnsupportedChains;

// Chain key arrays ----------------------------
// The `[T, ..T[]]` pattern used here allows the values to be used with Zod's .enum() method

export const ALL_CHAIN_KEYS = Object.keys(allChains) as [
  AllChainKey,
  ...AllChainKey[],
];

export const CHAIN_KEYS = Object.keys(chains) as [ChainKey, ...ChainKey[]];
export const UNSUPPORTED_CHAIN_KEYS = Object.keys(unsupportedChains) as [
  UnsupportedChainKey,
  ...UnsupportedChainKey[],
];

export const MAINNET_CHAIN_KEYS = Object.entries(chains)
  .filter(([_, c]) => c.isMainnet)
  .map(([key]) => key) as [MainnetChainKey, ...MainnetChainKey[]];

export const EVM_CHAIN_KEYS = Object.entries(chains)
  .filter(([_, c]) => c.runtime === 'evm')
  .map(([key]) => key) as [EvmChainKey, ...EvmChainKey[]];

export const EVM_MAINNET_CHAIN_KEYS = Object.entries(chains)
  .filter(([_, c]) => c.runtime === 'evm' && c.isMainnet)
  .map(([key]) => key) as [EvmMainnetChainKey, ...EvmMainnetChainKey[]];

export const SVM_CHAIN_KEYS = Object.entries(chains)
  .filter(([_, c]) => c.runtime === 'svm')
  .map(([key]) => key) as [SvmChainKey, ...SvmChainKey[]];

export const NATIVE_CHAIN_KEYS = Object.entries(chains)
  .filter(
    ([_, c]) =>
      c.runtime === 'evm' ||
      c.runtime === 'svm' ||
      c.runtime === 'sui' ||
      c.runtime === 'near'
  )
  .map(([key]) => key) as [NativeChainKey, ...NativeChainKey[]];

export const WALLET_CHAIN_KEYS = Object.entries(chains)
  .filter(([_, c]) => c.features.wallet.supported)
  .map(([key]) => key) as [WalletChainKey, ...WalletChainKey[]];

export const VAULT_CHAIN_KEYS = Object.entries(chains)
  .filter(([_, c]) => c.features.vault.supported)
  .map(([key]) => key) as [VaultChainKey, ...VaultChainKey[]];

export const EVM_VAULT_CHAIN_KEYS = Object.entries(chains)
  .filter(([_, c]) => c.runtime === 'evm' && c.features.vault.supported)
  .map(([key]) => key) as [EvmVaultChainKey, ...EvmVaultChainKey[]];

export const EVM_MAINNET_WALLET_CHAIN_KEYS = Object.entries(chains)
  .filter(
    ([_, c]) =>
      c.runtime === 'evm' && c.features.wallet.supported && c.isMainnet
  )
  .map(([key]) => key) as [
  EvmMainnetWalletChainKey,
  ...EvmMainnetWalletChainKey[],
];

export const SVM_MAINNET_WALLET_CHAIN_KEYS = Object.entries(chains)
  .filter(
    ([_, c]) =>
      c.runtime === 'svm' && c.features.wallet.supported && c.isMainnet
  )
  .map(([key]) => key) as [
  SvmMainnetWalletChainKey,
  ...SvmMainnetWalletChainKey[],
];

/**
 * Maps DefiLlama chain names to ChainKey.
 * Derived from chain configs where services.defiLlama is defined.
 * All keys are lowercased because DefiLlama stores them inconsistently
 * e.g. there are rows with both chain=Hyperevm and chain=hyperevm
 */
export const CHAIN_KEYS_BY_DEFILLAMA_CHAIN: Partial<Record<string, ChainKey>> =
  Object.entries(chains).reduce(
    (acc, [chainKey, config]) => {
      const defiLlamaConfig = config.services.defiLlama;
      if (defiLlamaConfig) {
        for (const key of defiLlamaConfig.chainKeys) {
          acc[key.toLowerCase()] = chainKey as ChainKey;
        }
      }
      return acc;
    },
    {} as Partial<Record<string, ChainKey>>
  );
